import { memo } from 'react';

import { type ListSlackIntegrationsObject } from '@air/api/types';
import { RadioGroup, RadioGroupItem } from '@air/primitive-radio-group';

import { type UpdateBoardSlackIntegrationProps } from '~/swr-hooks/slack/useUpdateBoardSlackIntegration';

type ConnectedToSlackTogglesProps = {
  integration: ListSlackIntegrationsObject;
  boardId: string;
  onUpdate: (params: UpdateBoardSlackIntegrationProps) => void;
  canManageSlackIntegration?: boolean;
};

const options = [
  {
    id: 'this-board',
    label: 'This board only',
  },
  {
    id: 'with-descendants',
    label: 'This board and all sub-boards',
  },
];

export const ConnectedToSlackToggles = memo(
  ({ canManageSlackIntegration, integration, boardId, onUpdate }: ConnectedToSlackTogglesProps) => {
    return (
      <div className="mt-2 rounded-6 border ads:border-ads-gray-5 border-gray-5 ads:bg-subtle bg-gray-3 px-4 py-3">
        <div className="font-medium ads:text-primary text-12 text-gray-12">Send notifications for:</div>
        <RadioGroup
          className="mt-2 gap-2"
          value={integration.includeDescendants ? 'with-descendants' : 'this-board'}
          disabled={!canManageSlackIntegration || integration.isCoveredByAncestor}
          // biome-ignore lint/performance/noJsxPropsBind: the radio change sends this integration's receiver, board, and channel
          onValueChange={(value) =>
            onUpdate({
              receiverId: integration.receiverId,
              boardId,
              includeDescendants: value === 'with-descendants',
              integrationId: integration.integrationId,
              channelName: integration.channelName,
            })
          }
        >
          {options.map(({ id, label }) => (
            <RadioGroupItem
              key={id}
              id={id}
              value={id}
              className="ads:text-primary text-14 text-gray-12 [&>label]:w-full"
            >
              {label}
            </RadioGroupItem>
          ))}
        </RadioGroup>
      </div>
    );
  },
);

ConnectedToSlackToggles.displayName = 'ConnectedToSlackToggles';
